import { useEffect, useRef, useState } from "react";
import useAuthStore from "@auth/store/useAuthStore";
import { fetchCallToken } from "@api/inboxRequest/inboxRequest";

interface TwilioCall {
  parameters?: { From?: string };
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  accept: () => void;
  reject: () => void;
  mute: (muted: boolean) => void;
}

interface TwilioDevice {
  register: () => void;
  destroy: () => void;
  disconnect: () => void;
  disconnectAll: () => void;
  updateToken: (token: string) => void;
  connect: (options: unknown) => Promise<TwilioCall>;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
}

interface SharedDeviceRef {
  current: TwilioDevice | null;
}

export const sharedDeviceRef: SharedDeviceRef = { current: null };

export const makeTwilioCall = async (
  connectOptions: unknown,
  callbacks: {
    onRinging?: (...args: unknown[]) => void;
    onAccept?: (...args: unknown[]) => void;
    onDisconnect?: (...args: unknown[]) => void;
    onError?: (...args: unknown[]) => void;
  } = {}
): Promise<TwilioCall | null> => {
  if (!sharedDeviceRef.current) {
    console.error("Twilio device not initialized");
    return null;
  }

  console.log("Making Twilio call with options:", connectOptions);

  const call = await sharedDeviceRef.current.connect(connectOptions);

  if (callbacks.onRinging) call.on("ringing", callbacks.onRinging);
  if (callbacks.onAccept) call.on("accept", callbacks.onAccept);
  if (callbacks.onDisconnect) call.on("disconnect", callbacks.onDisconnect);
  if (callbacks.onError) call.on("error", callbacks.onError);

  return call;
};

export function useTwilioDevice(
  token: string | null | undefined,
  incomingConnection: TwilioCall | null,
  setIncomingConnection: (call: TwilioCall | null) => void,
  isOutgoingObj: unknown,
  setIsOutgoingObj: ((val: null) => void) | null
) {
  const deviceRef = sharedDeviceRef;
  const [deviceInstance, setDeviceInstance] = useState<TwilioDevice | null>(null);
  const [callFrom, setCallFrom] = useState<string | null>(null);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [maxAttempts] = useState(3);
  const [connectionFailed, setConnectionFailed] = useState(false);
  const { user, token: authStoreToken } = useAuthStore();

  const getToken = async (): Promise<string | null> => {
    const { status, data } = await fetchCallToken(authStoreToken);
    if (status) {
      return (data as { token?: string })?.token ?? null;
    }
    return null;
  };

  useEffect(() => {
    let device: TwilioDevice | undefined;

    if (connectionFailed || connectionAttempts >= maxAttempts) {
      console.log(
        `Twilio connection aborted: Max attempts (${maxAttempts}) reached`
      );
      return;
    }

    if (typeof window !== "undefined" && token) {
      console.log(
        `Twilio connection attempt ${connectionAttempts + 1} of ${maxAttempts}`
      );

      import("@twilio/voice-sdk")
        .then(({ Device }) => {
          device = new Device(token) as unknown as TwilioDevice;
          device.register();
          deviceRef.current = device;

          device.on("registered", () => {
            console.log("Twilio device ready - connection successful");
            setConnectionAttempts(0);
            setConnectionFailed(false);
          });

          device.on("tokenWillExpire", async () => {
            const newToken = await getToken();
            if (newToken) {
              device!.updateToken(newToken);
            }
          });

          device.on("incoming", (call: unknown) => {
            const twilioCall = call as TwilioCall;
            console.log("Entrando", twilioCall);
            setIncomingConnection(twilioCall);
            setCallFrom(twilioCall.parameters?.From ?? null);

            twilioCall.on("disconnect", () => {
              setIncomingConnection(null);
            });
            twilioCall.on("cancel", () => {
              setIncomingConnection(null);
            });
            twilioCall.on("reject", () => {
              setIncomingConnection(null);
            });
          });

          device.on("disconnect", () => {
            setIncomingConnection(null);
          });

          device.on("error", async (err: { code?: number }) => {
            console.error("Twilio connection error:", err);

            if (err.code === 20101 || err.code === 31205) {
              const newToken = await getToken();
              if (newToken) {
                device!.updateToken(newToken);
                return;
              }
            }

            const newAttempts = connectionAttempts + 1;
            setConnectionAttempts(newAttempts);

            if (newAttempts >= maxAttempts) {
              console.error(
                `Twilio connection failed after ${maxAttempts} attempts. Stopping reconnection attempts.`
              );
              setConnectionFailed(true);
            } else {
              console.log(
                `Twilio will retry connection. Attempt ${
                  newAttempts + 1
                } of ${maxAttempts} will start shortly.`
              );
            }
          });
        })
        .catch((error: unknown) => {
          console.error("Error importing @twilio/voice-sdk:", error);
          const newAttempts = connectionAttempts + 1;
          setConnectionAttempts(newAttempts);

          if (newAttempts >= maxAttempts) {
            console.error(
              `Twilio import failed after ${maxAttempts} attempts. Stopping reconnection attempts.`
            );
            setConnectionFailed(true);
          }
        });
    }

    return () => {
      if (device) {
        device.disconnectAll();
        device.destroy();
        if (deviceRef.current === device) {
          deviceRef.current = null;
        }
      }
    };
  }, [token, connectionAttempts, maxAttempts, connectionFailed]);

  const initializeDevice = (): void => {
    if (deviceRef.current) {
      return;
    }
  };

  const resetConnection = (): void => {
    console.log("Manually resetting Twilio connection attempts");
    setConnectionAttempts(0);
    setConnectionFailed(false);
  };

  const acceptCall = (): void => {
    if (incomingConnection) {
      incomingConnection.accept();
    }
  };

  const rejectCall = (): void => {
    if (incomingConnection) {
      incomingConnection.reject();
      setIncomingConnection(null);
    }
  };

  const muteCall = (): void => {
    if (incomingConnection) {
      incomingConnection.mute(true);
    }
  };

  const unmuteCall = (): void => {
    if (incomingConnection) {
      incomingConnection.mute(false);
    }
  };

  const hangUpCall = (): void => {
    if (deviceRef.current) {
      deviceRef.current.disconnectAll();
    }
    setIncomingConnection(null);
    if (typeof setIsOutgoingObj === "function") setIsOutgoingObj(null);
  };

  return {
    device: deviceRef.current,
    initializeDevice,
    resetConnection,
    acceptCall,
    rejectCall,
    hangUpCall,
    callFrom,
    muteCall,
    unmuteCall,
    connectionAttempts,
    connectionFailed,
    maxAttempts,
  };
}
